import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, FileText, Search, Minus } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-authored manual invoice/order dialog.
 * Use case: phone sales, wholesale one-offs, cash/wire transactions that
 * bypass the storefront checkout. Reuses the same server-side price/variant
 * resolution as POST /orders so no admin typo can drift from catalog pricing.
 *
 * iter_133 — supports minimize: while `open` is true, if the parent hands us
 * a `draftRef` we hydrate initial state from it and write every user change
 * back to it. That way when the parent hides the dialog (minimize action)
 * and re-opens it later, the form comes back intact.
 */
export default function ManualInvoiceDialog({ open, onOpenChange, onCreated, onMinimize, draftRef }) {
  const stash = () => draftRef?.current || null;

  // Buyer identity
  const [customerName, setCustomerName] = useState(() => stash()?.customerName || "");
  const [customerEmail, setCustomerEmail] = useState(() => stash()?.customerEmail || "");
  // Shipping address
  const [addr, setAddr] = useState(() => stash()?.addr || { label: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
  // Line items
  const [lines, setLines] = useState(() => stash()?.lines || []);   // {product, quantity, variant, variant_mg}
  const [productQuery, setProductQuery] = useState("");
  const [productPool, setProductPool] = useState(() => stash()?.productPool || []);
  // Order controls
  const [paymentMethod, setPaymentMethod] = useState(() => stash()?.paymentMethod || "manual");
  const [shippingOverride, setShippingOverride] = useState(() => stash()?.shippingOverride || "");
  const [freeShipping, setFreeShipping] = useState(() => stash()?.freeShipping ?? false);
  // iter185.5 Phase 2.1 — explicit fulfillment-mode selector on manual orders.
  const [fulfillmentMode, setFulfillmentMode] = useState(() => stash()?.fulfillmentMode || "carrier");
  const [pickupNotes, setPickupNotes] = useState(() => stash()?.pickupNotes || "");
  const [discountCode, setDiscountCode] = useState(() => stash()?.discountCode || "");
  const [adminNotes, setAdminNotes] = useState(() => stash()?.adminNotes || "");
  const [markPaid, setMarkPaid] = useState(() => stash()?.markPaid ?? true);
  const [sendConfirmation, setSendConfirmation] = useState(() => stash()?.sendConfirmation ?? false);
  const [busy, setBusy] = useState(false);
  // Select-tier vial-quantity ladder (fetched once — applies to every Select
  // line's 1/3/5-Vial pricing preview). Server is still authoritative.
  const [selectLadder, setSelectLadder] = useState(() => stash()?.selectLadder || { 3: 0.05, 5: 0.10 });

  useEffect(() => {
    if (!open) return;
    // If we have a stashed draft, don't reset — just refresh the product pool
    // in case the catalog changed while we were minimized.
    const hasDraft = !!stash();
    if (!hasDraft) {
      // Reset on fresh open
      setCustomerName(""); setCustomerEmail("");
      setAddr({ label: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US" });
      setLines([]);
      setProductPool([]);
      setPaymentMethod("manual"); setShippingOverride(""); setDiscountCode(""); setAdminNotes("");
      setMarkPaid(true); setSendConfirmation(false);
    }
    setProductQuery("");
    // Fetch product pool for typeahead (always refresh on open)
    api.get("/products").then(({ data }) => setProductPool(Array.isArray(data) ? data : [])).catch(() => {});
    // Fetch Select-tier ladder for pricing preview
    api.get("/select-ladder").then(({ data }) => setSelectLadder({ 3: Number(data.discount_3vial || 0), 5: Number(data.discount_5vial || 0), 10: Number(data.discount_10vial || 0) })).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Sync every state change back to the parent's draftRef so a subsequent
  // minimize→expand round-trip restores the form perfectly.
  useEffect(() => {
    if (!draftRef) return;
    draftRef.current = {
      customerName, customerEmail, addr, lines, productPool,
      paymentMethod, shippingOverride, freeShipping, discountCode, adminNotes,      markPaid, sendConfirmation, selectLadder,
    };
  }, [
    draftRef, customerName, customerEmail, addr, lines, productPool,
    paymentMethod, shippingOverride, freeShipping, discountCode, adminNotes,    markPaid, sendConfirmation, selectLadder,
  ]);

  // Filter products by query (name / sku / slug)
  const filtered = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return productPool
      .filter((p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.slug || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [productQuery, productPool]);

  // Case-insensitive, whitespace-tolerant tier check. Defensive against
  // legacy DB rows that may store "Select" / " select " / etc.
  const isSelectTier = (product) => String(product?.tier || "").trim().toLowerCase() === "select";

  const addLine = (product) => {
    // Default the variant selection: prefer first priced mg-variant, and for
    // Select tier ALSO default the 1/3/5-Vial variant to 1vial (ladder-based).
    let variant = null;
    let variant_mg = null;
    if (Array.isArray(product.variants) && product.variants.length > 0) {
      const avail = product.variants.find((v) => v && v.price != null && v.in_stock !== false)
        || product.variants.find((v) => v && v.price != null);
      if (avail) variant_mg = String(avail.mg);
    }
    if (isSelectTier(product)) {
      variant = "1vial";
    }
    setLines((prev) => [...prev, { product, quantity: 1, variant, variant_mg }]);
    setProductQuery("");
  };

  const setLineQty = (idx, n) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, quantity: Math.max(1, n) } : l));
  const setLineVariant = (idx, field, value) => setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  // Compute a client-side price preview (server is authoritative on submit).
  // Select tier: unit × N × (1 - ladder[N]) where N ∈ {1,3,5}. Supply/legacy
  // tier: mg-variant price or flat product.price.
  const previewUnitPrice = (line) => {
    const p = line.product;
    if (isSelectTier(p)) {
      let unit = Number(p.price || 0);
      if (line.variant_mg && Array.isArray(p.variants)) {
        const m = p.variants.find((v) => String(v.mg) === String(line.variant_mg));
        if (m && m.price != null) unit = Number(m.price);
      }
      const n = line.variant === "3vial" ? 3 : line.variant === "5vial" ? 5 : line.variant === "10vial" ? 10 : 1;
      const disc = Number(selectLadder[n] || 0);
      return +(unit * n * (1 - disc)).toFixed(2);
    }
    if (line.variant_mg && Array.isArray(p.variants)) {
      const m = p.variants.find((v) => String(v.mg) === String(line.variant_mg));
      if (m && m.price != null) return Number(m.price);
    }
    return Number(p.price || 0);
  };
  const subtotalPreview = lines.reduce((sum, l) => sum + previewUnitPrice(l) * l.quantity, 0);

  const submit = async () => {
    // Basic client-side validation
    if (!customerName.trim()) { toast.error("Customer name is required"); return; }
    if (!customerEmail.trim()) { toast.error("Customer email is required"); return; }
    if (!addr.line1.trim() || !addr.city.trim() || !addr.state.trim() || !addr.postal_code.trim()) {
      toast.error("Shipping address is incomplete"); return;
    }
    if (lines.length === 0) { toast.error("Add at least one product"); return; }

    const payload = {
      customer_name: customerName.trim(),
      customer_email: customerEmail.trim(),
      address: addr,
      items: lines.map((l) => ({
        product_id: l.product.id,
        name: l.product.name,
        // price is server-authoritative — sending a placeholder is fine
        price: previewUnitPrice(l),
        quantity: l.quantity,
        image_url: l.product.image_url,
        variant: l.variant,
        variant_mg: l.variant_mg,
      })),
      payment_method: paymentMethod,
      shipping_override: freeShipping ? 0 : (shippingOverride === "" ? null : Number(shippingOverride)),
      discount_code: discountCode.trim() || null,
      admin_notes: adminNotes.trim() || null,
      mark_paid: markPaid,
      send_confirmation_email: sendConfirmation,
      // iter185.5 Phase 2.1 — fulfillment mode
      fulfillment_mode: fulfillmentMode,
      pickup_notes: fulfillmentMode === "local_pickup" ? (pickupNotes || null) : null,
    };

    setBusy(true);
    try {
      const { data } = await api.post("/admin/orders/manual", payload);
      toast.success(`Order ${data.order_number} created — $${(data.total ?? 0).toFixed(2)}`);
      onCreated?.(data);
      // Clear the stashed draft so the next "New manual order" starts blank.
      if (draftRef) draftRef.current = null;
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Order creation failed"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "rounded-none border-borderSubtle bg-white focus:ring-0 focus:border-charcoal";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-3xl bg-bone border-borderSubtle max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2">
            <FileText size={20} strokeWidth={2}/> New manual order
          </DialogTitle>
        </DialogHeader>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            className="absolute right-12 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            data-testid="mi-minimize-btn"
            title="Minimize — keep this order draft and come back later"
            aria-label="Minimize manual order"
          >
            <Minus className="h-4 w-4" strokeWidth={2}/>
          </button>
        )}
        <p className="text-xs text-charcoal/75 -mt-2 mb-4">
          For phone / wholesale / one-off sales. Reuses server-side pricing so line totals always match the catalog.
        </p>

        {/* Buyer */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <Label className="overline">Customer name</Label>
            <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className={inputCls} data-testid="mi-customer-name"/>
          </div>
          <div>
            <Label className="overline">Customer email</Label>
            <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} className={inputCls} data-testid="mi-customer-email"/>
          </div>
        </div>

        {/* Address */}
        <div className="mb-4">
          <Label className="overline mb-2 block">Shipping address</Label>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Line 1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className={inputCls} data-testid="mi-addr-line1"/>
            <Input placeholder="Line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} className={inputCls} data-testid="mi-addr-line2"/>
            <Input placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={inputCls} data-testid="mi-addr-city"/>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className={inputCls} data-testid="mi-addr-state"/>
              <Input placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} className={inputCls} data-testid="mi-addr-zip"/>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="mb-4">
          <Label className="overline mb-2 block">Items</Label>
          <div className="relative mb-2">
            <div className="flex items-center gap-2">
              <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/75"/>
              <Input
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Search product by name, SKU, or slug…"
                className={`${inputCls} pl-9`}
                data-testid="mi-product-search"
              />
            </div>
            {filtered.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-borderSubtle max-h-56 overflow-y-auto shadow" data-testid="mi-product-suggestions">
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addLine(p)}
                    className="w-full text-left px-3 py-2 hover:bg-bone/50 text-sm flex justify-between gap-3 border-b border-borderSubtle last:border-b-0"
                    data-testid={`mi-add-product-${p.slug}`}
                  >
                    <span className="truncate">
                      <span className="font-serif">{p.name}</span>
                      <span className="text-charcoal/75 font-mono text-[11px] ml-2 uppercase tracking-widest">{p.sku}</span>
                      {String(p.tier || "").trim().toLowerCase() === "select" && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] tracking-widest bg-charcoal text-bone rounded-sm">SELECT</span>
                      )}
                    </span>
                    <span className="font-mono text-xs text-charcoal/75">${(p.price ?? 0).toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length === 0 ? (
            <p className="text-xs text-charcoal/75 italic" data-testid="mi-lines-empty">No products yet. Search above to add.</p>
          ) : (
            <div className="space-y-2" data-testid="mi-lines">
              {lines.map((l, idx) => {
                const p = l.product;
                const hasMgVariants = Array.isArray(p.variants) && p.variants.length > 0;
                const isSelect = isSelectTier(p);
                const laddered = isSelect && (l.variant === "3vial" || l.variant === "5vial" || l.variant === "10vial");
                return (
                  <div key={idx} className="grid grid-cols-[1fr_auto_72px_auto_auto] gap-3 items-end bg-white border border-borderSubtle rounded-xl p-3" data-testid={`mi-line-${idx}`}>
                    <div className="min-w-0">
                      <p className="font-serif text-sm truncate">{p.name}</p>
                      <p className="text-[11px] uppercase tracking-widest text-charcoal/75 font-mono">{p.sku}{isSelect && <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] tracking-widest bg-charcoal text-bone rounded-sm">SELECT</span>}</p>
                    </div>
                    <div className="flex items-end gap-3">
                      {hasMgVariants && (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Strength</span>
                          <select
                            value={l.variant_mg || ""}
                            onChange={(e) => setLineVariant(idx, "variant_mg", e.target.value)}
                            className={`${inputCls} text-xs h-9`}
                            data-testid={`mi-line-mg-${idx}`}
                          >
                            {p.variants.filter((v) => v && v.price != null).map((v) => (
                              <option key={String(v.mg)} value={String(v.mg)}>{v.mg}mg · ${Number(v.price).toFixed(2)}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {isSelect && (
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Kit size</span>
                          <select
                            value={l.variant || "1vial"}
                            onChange={(e) => setLineVariant(idx, "variant", e.target.value)}
                            className={`${inputCls} text-xs h-9 min-w-[8.5rem] ${laddered ? "border-oxblood ring-1 ring-oxblood/30" : ""}`}
                            data-testid={`mi-line-variant-${idx}`}
                          >
                            <option value="1vial">1 Vial</option>
                            <option value="3vial">3 Vials — {Math.round((selectLadder[3]||0)*100)}% off</option>
                            <option value="5vial">5 Vials — {Math.round((selectLadder[5]||0)*100)}% off</option>
                            <option value="10vial">10 Vials — {Math.round((selectLadder[10]||0)*100)}% off</option>
                          </select>
                        </div>
                      )}
                      {!hasMgVariants && !isSelect && (
                        <span className="text-[11px] uppercase tracking-widest text-charcoal/75 font-mono">flat</span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Qty</span>
                      <Input
                        type="number"
                        min="1"
                        value={l.quantity}
                        onChange={(e) => setLineQty(idx, Number(e.target.value))}
                        className={`${inputCls} text-center h-9`}
                        data-testid={`mi-line-qty-${idx}`}
                      />
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-[10px] uppercase tracking-widest text-charcoal/75 font-mono mb-1">Line total</span>
                      <span className="font-mono text-sm w-24 text-right h-9 flex items-center justify-end" data-testid={`mi-line-total-${idx}`}>${(previewUnitPrice(l) * l.quantity).toFixed(2)}</span>
                    </div>
                    <button onClick={() => removeLine(idx)} className="text-charcoal/75 hover:text-oxblood p-1 self-center" data-testid={`mi-line-remove-${idx}`} aria-label="Remove">
                      <Trash2 size={14} strokeWidth={1.5}/>
                    </button>
                  </div>
                );
              })}
              <div className="flex justify-end text-sm mt-2">
                <span className="text-charcoal/75 mr-3">Subtotal preview:</span>
                <span className="font-mono font-semibold" data-testid="mi-subtotal-preview">${subtotalPreview.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        {/* iter185.5 Phase 2.1 — Fulfillment mode selector */}
        <div className="mb-4">
          <Label className="overline">Fulfillment mode</Label>
          <div className="mt-1.5 inline-flex rounded-full border border-borderSubtle bg-white p-1" data-testid="mi-fulfillment-mode">
            {[
              { key: "carrier",        label: "Carrier Shipping" },
              { key: "local_delivery", label: "Local Delivery" },
              { key: "local_pickup",   label: "Local Pickup" },
            ].map((opt) => {
              const active = fulfillmentMode === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setFulfillmentMode(opt.key)}
                  className={`px-3.5 py-1.5 text-[12px] uppercase tracking-[0.18em] rounded-full transition-colors ${
                    active ? "bg-oxblood text-bone" : "text-charcoal/75 hover:text-charcoal"
                  }`}
                  data-testid={`mi-fulfillment-${opt.key}`}
                >{opt.label}</button>
              );
            })}
          </div>
          {fulfillmentMode === "local_pickup" && (
            <div className="mt-3">
              <Label className="overline">Pickup notes (optional)</Label>
              <Input value={pickupNotes} onChange={(e) => setPickupNotes(e.target.value)} className={inputCls} placeholder="e.g. front door · 4pm Fri · call on arrival" data-testid="mi-pickup-notes"/>
            </div>
          )}
        </div>

        {/* Order controls */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <Label className="overline">Payment method</Label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={`${inputCls} w-full`} data-testid="mi-payment-method">
              <option value="manual">Manual (cash/wire/other)</option>
              <option value="venmo">Venmo</option>
              <option value="cashapp">Cash App</option>
              <option value="stripe">Stripe</option>
            </select>
          </div>
          <div>
            <Label className="overline">Shipping override</Label>
            <Input type="number" step="0.01" placeholder="Auto if blank" value={freeShipping ? "0.00" : shippingOverride} onChange={(e) => setShippingOverride(e.target.value)} className={inputCls} disabled={freeShipping} data-testid="mi-shipping-override"/>
            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer text-xs">
              <input type="checkbox" className="accent-oxblood" checked={freeShipping} onChange={(e) => setFreeShipping(e.target.checked)} data-testid="mi-free-shipping" />
              <span className="text-charcoal">Free shipping (waive fee)</span>
            </label>
          </div>
          <div>
            <Label className="overline">Discount code</Label>
            <Input placeholder="Optional" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} className={inputCls} data-testid="mi-discount-code"/>
          </div>
        </div>

        <div className="mb-4">
          <Label className="overline">Admin note (internal)</Label>
          <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className={inputCls} placeholder="e.g. 'Phone order — Dr. Green wholesale'" data-testid="mi-admin-notes"/>
        </div>

        <div className="flex items-center gap-4 mb-4 text-xs">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} data-testid="mi-mark-paid"/>
            Mark as paid on creation (skip pending)
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={sendConfirmation} onChange={(e) => setSendConfirmation(e.target.checked)} data-testid="mi-send-confirmation"/>
            Email buyer a confirmation
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 border border-borderSubtle text-charcoal tracking-[0.2em] text-xs uppercase hover:border-charcoal transition-colors"
            data-testid="mi-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-5 py-2 bg-oxblood text-bone tracking-[0.2em] text-xs uppercase hover:bg-charcoal transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="mi-create"
          >
            <Plus size={12} strokeWidth={2}/> {busy ? "Creating…" : "Create Order"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
